// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Type, CheckSquare, List, ChevronDown } from 'lucide-react';
import { isMoneyField, maskBRL } from '../s26Sync';

function getFieldIcon(t) {
  if (t === 'PDFCheckBox') return CheckSquare;
  if (t === 'PDFDropdown' || t === 'PDFOptionList') return List;
  return Type;
}

function hasValue(value) {
  if (typeof value === 'boolean') return true;
  return !!value && String(value).trim() !== '';
}

export default function AuditFieldRenderer({ field, value, onChange }) {
  const Icon = getFieldIcon(field.type);
  const filled = hasValue(value);
  const money = field.type === 'PDFTextField' && !field.isMultiline && isMoneyField(field.name);

  return (
    <div
      className={`p-3 rounded-lg border transition-colors ${
        filled
          ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20'
          : 'border-border bg-card'
      }`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
        <Label className="text-sm font-medium leading-snug">{field.label}</Label>
      </div>
      {field.type === 'PDFTextField' &&
        (field.isMultiline ? (
          <textarea
            data-testid={`field-${field.name}`}
            className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground resize-y min-h-[60px]"
            value={value || ''}
            onChange={e => onChange(field.name, e.target.value)}
            placeholder="Digite aqui..."
          />
        ) : (
          <Input
            data-testid={`field-${field.name}`}
            value={value || ''}
            onChange={e => onChange(field.name, money ? maskBRL(e.target.value) : e.target.value)}
            placeholder={money ? '0,00' : 'Digite aqui...'}
            inputMode={money ? 'numeric' : undefined}
            className={money ? 'text-sm text-right' : 'text-sm'}
          />
        ))}
      {field.type === 'PDFCheckBox' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`field-${field.name}`}
            checked={!!value}
            onChange={e => onChange(field.name, e.target.checked)}
            className="w-4 h-4 rounded border-input"
          />
          <span className="text-sm text-foreground">Marcado</span>
        </label>
      )}
      {(field.type === 'PDFDropdown' || field.type === 'PDFOptionList') && (
        <div className="relative">
          <select
            data-testid={`field-${field.name}`}
            value={value || ''}
            onChange={e => onChange(field.name, e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground appearance-none pr-8"
          >
            <option value="">Selecione...</option>
            {field.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      )}
      {field.type === 'PDFRadioGroup' && (
        <div className="space-y-1">
          {field.options.map(opt => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={field.name}
                value={opt}
                checked={value === opt}
                onChange={e => onChange(field.name, e.target.value)}
                className="w-4 h-4"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
